/**
 * Shared session identity for in-house telemetry.
 *
 * Reuses the same `page_session_id` / `lx_visitor_id` values the existing
 * page_visits analytics use, so errors, replays and business events can be
 * joined to visits and funnels without a second identity system.
 */
import { getVisitorId } from "@/lib/visitorId";

let inMemorySessionId: string | null = null;

export function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem("page_session_id");
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem("page_session_id", sid);
    }
    return sid;
  } catch {
    if (!inMemorySessionId) inMemorySessionId = crypto.randomUUID();
    return inMemorySessionId;
  }
}

export { getVisitorId };

export function getDeviceType(): string {
  try {
    const ua = navigator.userAgent;
    if (/Mobi|Android/i.test(ua)) return /iPad|Tablet/i.test(ua) ? "tablet" : "mobile";
    return "desktop";
  } catch {
    return "unknown";
  }
}

/**
 * App release marker used to group errors and web vitals by deploy.
 *
 * `__APP_BUILD_ID__` is stamped per build by buildStampPlugin in vite.config.ts
 * (`<version>-<buildTime>`), which is what makes release-over-release vitals
 * comparisons work. VITE_APP_VERSION is not set at build time, so relying on it
 * alone reported every production sample as "unknown".
 */
export function getRelease(): string {
  try {
    if (typeof __APP_BUILD_ID__ === "string" && __APP_BUILD_ID__) {
      return __APP_BUILD_ID__;
    }
  } catch {
    /* build-time define missing (e.g. plain vitest run) */
  }
  try {
    return (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "unknown";
  } catch {
    return "unknown";
  }
}


/**
 * Routes where nothing is ever recorded: credentials, OTP entry and payment
 * flows. Errors are still reported from these routes, but no DOM replay.
 */
const BLOCKED_ROUTE_PATTERNS = [
  /^\/auth/i,
  /^\/login/i,
  /^\/signup/i,
  /^\/reset-password/i,
  /^\/forgot-password/i,
  /otp/i,
  /checkout/i,
  /payment/i,
  /razorpay/i,
];

export function isReplayBlockedRoute(path = window.location.pathname): boolean {
  return BLOCKED_ROUTE_PATTERNS.some((re) => re.test(path));
}

/**
 * Replay sampling: only ~10% of sessions are recorded, so replay writes don't
 * compete with product writes. The decision is sticky per session (stored
 * alongside the session id) so a sampled session records end-to-end.
 */
const REPLAY_SAMPLE_RATE = 0.1;
const SAMPLE_KEY = "lx_replay_sampled";
let inMemorySampled: boolean | null = null;

export function isReplaySampled(): boolean {
  try {
    const stored = sessionStorage.getItem(SAMPLE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    const sampled = Math.random() < REPLAY_SAMPLE_RATE;
    sessionStorage.setItem(SAMPLE_KEY, sampled ? "1" : "0");
    return sampled;
  } catch {
    if (inMemorySampled === null) inMemorySampled = Math.random() < REPLAY_SAMPLE_RATE;
    return inMemorySampled;
  }
}

